// src/utils/menu/specialsUtils.js

/**
 * Utilities for handling specials logic
 */

/**
 * Filter special products based on active specials
 */
export const filterSpecialProductsByActiveSpecials = (specialProductsIds, activeSpecialsList, showEmptySpecials) => {
    if (!specialProductsIds || !activeSpecialsList) return {};
    if (showEmptySpecials) return specialProductsIds;
    
    const validSpecialIds = new Set(activeSpecialsList.map(special => special.id));
    
    return Object.fromEntries(
        Object.entries(specialProductsIds).filter(([specialId]) => validSpecialIds.has(specialId))
    );
};

/**
 * Get the most recent special for a product
 */
export const getMostRecentSpecialForProduct = (productId, specialProductsIds, specialsList) => {
    if (!specialsList || !specialProductsIds) return null;
    
    // Find all specials that include this product
    const matchingSpecialEntries = Object.entries(specialProductsIds)
        .filter(([_, productIds]) => productIds.includes(productId));
    
    if (matchingSpecialEntries.length === 0) return null;
    if (matchingSpecialEntries.length === 1) return matchingSpecialEntries[0][0];
    
    // If multiple specials, find the most recent one
    const specialIds = matchingSpecialEntries.map(([specialId]) => specialId);
    const matchingSpecials = specialsList.filter(special => specialIds.includes(special.id));
    
    // Sort by start date (most recent first)
    const sortedSpecials = sortSpecialsByDate(matchingSpecials);
    
    return sortedSpecials.length > 0 ? sortedSpecials[0].id : null;
};

/**
 * Sort specials by start date (most recent first)
 */
export const sortSpecialsByDate = (specials) => {
    return [...specials].sort((a, b) => {
        const aStart = a?.scheduleConfiguration?.startStamp;
        const bStart = b?.scheduleConfiguration?.startStamp;
        
        if (!aStart && !bStart) return 0;
        if (!aStart) return 1;
        if (!bStart) return -1;
        
        return new Date(bStart).getTime() - new Date(aStart).getTime();
    });
};

/**
 * Check if a special is currently active
 */
export const isSpecialActive = (special) => {
    if (!special?.scheduleConfiguration) return true;
    
    const now = new Date();
    const { startStamp, endStamp } = special.scheduleConfiguration;
    
    if (startStamp && new Date(startStamp) > now) return false;
    if (endStamp && new Date(endStamp) < now) return false;
    
    return true;
};

/**
 * Get products for a specific special
 */
export const getProductsForSpecial = (specialId, specialProductsIds) => {
    return specialProductsIds[specialId] || [];
};

/**
 * Check if a product has any specials
 */
export const productHasSpecials = (productId, specialProductsIds) => {
    return Object.values(specialProductsIds).some(productIds => 
        productIds.includes(productId)
    );
};

/**
 * Get all special IDs for a product
 */
export const getSpecialIdsForProduct = (productId, specialProductsIds) => {
    return Object.entries(specialProductsIds)
        .filter(([_, productIds]) => productIds.includes(productId))
        .map(([specialId]) => specialId);
};

/**
 * Filter brands that have special products
 */
export const filterSpecialBrands = (brands, specialBrands) => {
    if (!specialBrands || specialBrands.length === 0) return brands;
    
    const specialBrandIds = new Set(specialBrands.map(b => b.id));
    return brands.filter(brand => specialBrandIds.has(brand.id));
};
